















































































































































































































































































































































import { defineComponent, ref, computed, onMounted, watch } from '@vue/composition-api';
import store from '@/store';

import { prompt } from '@/components/Prompt';
import { VideoList, UserLevel } from '@/types';
import { manageVideoFilesMenu } from '../state';
import { useClient, useVideos } from '@/use';
import { isAxiosError } from '@/api';
import { snackbar } from '@/components/GlobalSnackbar';

const headers = [
  { text: 'Video Filename', value: '_id' },
  { text: 'Race', value: 'subject_race', sortable: false },
  { text: 'Sex', value: 'subject_sex', sortable: false },
  { text: 'Gestation Age', value: 'gestation_age' },
  { text: 'Age', value: 'age' },
  { text: 'Procedure', value: 'procedure', sortable: false },
  { text: 'Observation Period', value: 'observation_period', sortable: false },
  { text: 'Data Source', value: 'data_source', sortable: false },
  { text: 'Details', value: 'details', sortable: false },
  { text: 'Referenced Frames', value: 'referenced_count' },
  { text: 'Total Frames', value: 'number_of_frames' },
  { text: 'Actions', value: 'actions', sortable: false },
];

export default defineComponent({

  name: 'ManageVideoFilesMenu',
  setup() {
    const client = useClient();
    const subjectRaces = computed(() => store.state.application.applicationData?.lists.subject_races);
    const subjectSex = computed(() => store.state.application.applicationData?.lists.subject_sex);
    const procedure = computed(() => store.state.application.applicationData?.lists.procedure);
    const observationPeriod = computed(() => store.state.application.applicationData?.lists.observation_period);
    const defaultSubjectSex = computed(() => (subjectSex.value && subjectSex.value[subjectSex.value.length - 1]) || '');
    const dataSources = computed(() => {
      // If user level is standard user, just return their allowed institutions
      if (client?.user?.user_level === UserLevel.User || client?.user?.user_level === UserLevel.Userhigh) {
        return client.user?.accessible_institutions;
      }

      // Otherwise return all possible
      return store.state.application.applicationData?.lists.institutions;
    });

    // Table and search
    const {
      loading,
      items,
      selected,
      fetch,
      videoTableInfo,
      videoTableOptions,
      itemsPerPage,
      search,
      filterSubjectRace,
      filterSubjectSex,
      filterProcedure,
      filterObservationPeriod,
      filterDataSource,
    } = useVideos();

    // Computed data sources from list of videos
    // const dataSources = computed(() => Array.from(items.value.reduce((set, cur) => set.add(cur.data_source), new Set())));

    function closeDialog() { manageVideoFilesMenu.value = false; }

    const selectedVideo = ref<VideoList | null>(null);
    const formValid = ref(false);

    // Edit Dialog
    const editSubjectRace = ref<string | null>(null);
    const editSubjectSex = ref<string>(defaultSubjectSex.value);
    const editGestationAgeWeek = ref<number>(0);
    const editGestationAgeDay = ref<number>(0);
    const editAgeDay = ref<number>(0);
    const editProcedure = ref<string | null>(null);
    const editObservationPeriod = ref<string | null>(null);
    const editDataSource = ref<string | null>(null);
    const editDetails = ref<string[]>([]);
    const editRemarks = ref<string | null>(null);
    const editDialogOpen = ref(false);

    const nonEmptyRule = (val: string) => (!!val || 'Field cannot be empty.');
    const maximum100Rule = (val: string) => (val && val.length <= 100) || 'Maximum 100 characters required';
    const min0Max6Rule = (val: number) => !val || (val >= 0 && val <= 6) || 'the number is out of range';
    const min0Rule = (val: number) => !val || (val >= 0) || 'the number is out of range';

    function openEditDialog(video: VideoList) {
      selectedVideo.value = video;
      editSubjectRace.value = video.subject_race;
      editSubjectSex.value = video.subject_sex || defaultSubjectSex.value;
      editGestationAgeWeek.value = video.gestation_age ? Math.floor(video.gestation_age / 7) : 0;
      editGestationAgeDay.value = video.gestation_age ? video.gestation_age - 7 * editGestationAgeWeek.value : 0;
      editAgeDay.value = video.age;
      editProcedure.value = video.procedure;
      editObservationPeriod.value = video.observation_period;
      editDataSource.value = video.data_source;
      editDetails.value = video.details || [''];
      editRemarks.value = video.remarks || null;

      editDialogOpen.value = true;
    }

    function closeEditDialog() {
      selectedVideo.value = null;
      editSubjectRace.value = null;
      editProcedure.value = null;
      editObservationPeriod.value = null;
      editDataSource.value = null;
      editDetails.value = [];

      editDialogOpen.value = false;
    }

    async function saveSelectedVideo() {
      if (!selectedVideo.value) { return; }

      const video: VideoList = {
        ...selectedVideo.value,
        data_source: editDataSource.value || selectedVideo.value.data_source,
        subject_race: editSubjectRace.value || selectedVideo.value.subject_race,
        subject_sex: editSubjectSex.value || selectedVideo.value.subject_sex,
        gestation_age: Number(editGestationAgeWeek.value) * 7 + Number(editGestationAgeDay.value),
        age: Number(editAgeDay.value),
        procedure: editProcedure.value || selectedVideo.value.procedure,
        observation_period: editObservationPeriod.value || selectedVideo.value.observation_period,
        details: editDetails.value || selectedVideo.value.details,
        remarks: editRemarks.value || selectedVideo.value.remarks,
      };

      const success = await client.updateVideoEntry(video);
      if (!success) {
        throw new Error('Attempted to update non-existant video!');
      }

      search.value = '';
      closeEditDialog();
      fetch();
    }

    // Delete Dialog
    const deleteDialogOpen = ref(false);
    function openDeleteDialog(video: VideoList) {
      selectedVideo.value = video;
      deleteDialogOpen.value = true;
    }

    function closeDeleteDialog() {
      selectedVideo.value = null;
      deleteDialogOpen.value = false;
    }

    watch(deleteDialogOpen, (open) => {
      if (!open) { closeDeleteDialog(); }
    });

    function handleClickRemove(counter: number) {
      editDetails.value.splice(counter, 1);
    }

    function handleClickAdd() {
      editDetails.value.push('');
    }

    async function deleteVideo(video: VideoList): Promise<boolean> {
      try {
        await client.deleteVideoEntry(video);
      } catch (err) {
        if (!isAxiosError(err)) {
          throw err;
        }

        // Dispatch failed snackbar
        snackbar.dispatch({
          // eslint-disable-next-line no-underscore-dangle
          text: `Datasets generated from video "${video._id}" still exist.`,
          options: { top: true, color: 'error' },
          timeout: 5000,
        });
        return false;
      }

      return true;
    }

    async function deleteSelectedVideo() {
      const video = selectedVideo.value;
      if (!video) { return; }

      const res = await deleteVideo(video);
      deleteDialogOpen.value = false;
      if (res) {
        // Dispatch successful snackbar
        snackbar.dispatch({
          // eslint-disable-next-line no-underscore-dangle
          text: `Video "${video._id}" successfully deleted.`,
          options: { top: true, color: 'success' },
          timeout: 2000,
        });
      }

      search.value = '';
      closeDeleteDialog();
      fetch();
    }

    async function deleteSelectedVideos() {
      if (!selected.value) {
        return;
      }

      // Check with user
      const proceed = await prompt.open({
        title: 'Delete all selected videos?',
        positiveText: 'Yes',
        negativeText: 'No',
      });
      if (!proceed) {
        return;
      }

      // Perform delete
      const res = await Promise.all(selected.value.map(
        (v) => deleteVideo(v),
      ));
      if (res.every((v) => v)) {
        // Dispatch successful snackbar
        snackbar.dispatch({
          text: `${selected.value.length} videos successfully deleted.`,
          options: { top: true, color: 'success' },
          timeout: 2000,
        });
      }

      search.value = '';
      selected.value = [];

      // Fetch new datasets
      fetch();
    }

    function videoDeleteDisabled(video: VideoList) {
      if (store.getters.isAdmin) {
        return false;
      }

      const user = client?.user;
      if (!user || video.owner === undefined) {
        return true;
      }

      // eslint-disable-next-line no-underscore-dangle
      return video.owner !== user._id;
    }

    // Fetch datasets on mount
    onMounted(fetch);

    return {
      headers,

      search,
      filterSubjectRace,
      filterSubjectSex,
      filterProcedure,
      filterObservationPeriod,
      filterDataSource,
      items,
      loading,
      selected,
      videoTableInfo,
      videoTableOptions,
      itemsPerPage,

      closeDialog,
      nonEmptyRule,
      maximum100Rule,
      min0Max6Rule,
      min0Rule,
      formValid,

      subjectRaces,
      subjectSex,
      procedure,
      observationPeriod,
      defaultSubjectSex,
      dataSources,

      selectedVideo,

      // Edit Dialog
      editSubjectRace,
      editSubjectSex,
      editGestationAgeWeek,
      editGestationAgeDay,
      editAgeDay,
      editProcedure,
      editObservationPeriod,
      editDataSource,
      editDetails,
      editRemarks,
      editDialogOpen,
      openEditDialog,
      closeEditDialog,
      saveSelectedVideo,

      // Delete Dialog
      videoDeleteDisabled,
      deleteDialogOpen,
      openDeleteDialog,
      closeDeleteDialog,
      deleteSelectedVideo,
      deleteSelectedVideos,

      handleClickRemove,
      handleClickAdd,
    };
  },
});
